// src/pages/FroggyChatPageStreaming.jsx
import React, { useState, useContext, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { AuthContext } from '../context/AuthContext';
import { useFacilityConsole } from '../context/FacilityConsoleContext';
import FroggyWorking from '../components/FroggyChatBubble/FroggyWorking';
import EscalateToSupportModal from '../components/EscalateToSupportModal';
import VoiceRecorder from '../components/froggerAI/VoiceRecorder';
import { streamingAPI } from '../services/streamingAPI';
import { sendFroggyVoiceMessage, getFroggyVoiceConfig } from '../aiApi';
import { nanoid } from 'nanoid';
import { scrollIntoViewMotionSafe } from '../utils/scrollHelpers';

// Markdown components: render <a> with new-tab + noreferrer for any
// external link the model cites. Internal links (relative paths into
// our own site) stay same-tab so customers don't lose chat context.
// Heading/list defaults from react-markdown render fine as-is.
//
// `img` handler (P-08-Images): renders markdown `![alt](url)` from
// getSpeciesImages but ONLY when the URL host is on a whitelist. The
// model could be tricked into rendering arbitrary `<img src>` via
// prompt injection — without the whitelist, a malicious caption
// stored in our KB could exfiltrate via image-src request to an
// attacker-controlled host. Whitelist: R2 CDN domain + the bare
// R2 bucket URL + Smithsonian's IIIF + our care guide page. Drop
// anything else (return null = render nothing) silently. Adversarial
// review MED-6 (2026-05-23).
const ALLOWED_IMAGE_HOST_REGEXES = [
  // Cloudflare R2 custom-domain CDN (configured via MEDIA_CDN_DOMAIN
  // env on the backend). Match prod naming (`media.feelingfroggy.llc`)
  // AND known env-suffixed variants. The env-suffix list is an explicit
  // allowlist rather than `[a-z0-9]+` so an internal subdomain we add
  // later for a non-image service (e.g. `cdn-api.feelingfroggy.llc`)
  // can't be coincidentally matched. Add the env name here when we
  // spin up a new environment.
  // Added 2026-05-25 after Cowork QA caught beta images silently
  // dropping — beta uses `media-beta.feelingfroggy.llc`.
  /^https:\/\/(cdn|media|images?)(?:-(?:beta|staging|dev|preview))?\.feelingfroggy\.llc(\/|$)/i,
  /^https:\/\/feelingfroggy\.llc\/static\//i,
  // NOTE: we intentionally do NOT whitelist the generic
  // *.r2.cloudflarestorage.com subdomain. Anyone can register an R2
  // bucket — an attacker-controlled bucket at
  // attacker.r2.cloudflarestorage.com/payload.jpg would pass that
  // regex. Production images go through MEDIA_CDN_DOMAIN, not the
  // raw R2 hostname. (Adversarial review HIGH-S1, 2026-05-23.)
  // Smithsonian IIIF — the source's own image URLs are CC0 and we
  // sometimes cite source_url directly without re-hosting.
  // Smithsonian doesn't allow arbitrary subdomain registration so
  // the suffix match is safe here.
  /^https:\/\/ids\.si\.edu\//i,
  /^https:\/\/[a-z0-9-]+\.si\.edu\//i,
];

function isAllowedImageHost(src) {
  if (!src || typeof src !== 'string') return false;
  return ALLOWED_IMAGE_HOST_REGEXES.some((re) => re.test(src));
}

const MARKDOWN_COMPONENTS = {
  a: ({ node, href, children, ...props }) => {
    const isExternal = /^https?:\/\//i.test(href || '');
    return (
      <a
        href={href}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...props}
      >
        {children}
      </a>
    );
  },
  img: ({ node, src, alt, ...props }) => {
    if (!isAllowedImageHost(src)) {
      // Silently drop. The model can still cite the image's source_url
      // as a clickable link via the `a` handler above — customers
      // can navigate to view it if they want, but we don't fetch
      // bytes from arbitrary hosts.
      return null;
    }
    return (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        referrerPolicy="no-referrer"
        className="froggy-chat-img"
        {...props}
      />
    );
  },
};

// NOTE: FroggyAI is customer-facing. Do NOT require FacilityUserRole.
// Always auto-resolve a facility (tenant/env/first) and never block the UI.

// Resolve a facility ID for FroggyAI (customer-facing).
// Precedence: selectedFacilityId (if staff set) → homepage facility → env default.
// Returns null only if ALL resolution methods fail - streaming will show appropriate error.
async function resolveFacilityId({ selectedFacilityId }) {
  // 1) Prefer a staff-selected facility if present
  if (selectedFacilityId && selectedFacilityId !== 'null' && selectedFacilityId !== 'undefined') {
    console.log('[FroggyAI] Using selectedFacilityId:', selectedFacilityId);
    return selectedFacilityId;
  }

  // 2) Get facility from homepage endpoint (always available, public)
  try {
    const r = await fetch('/api/store/homepage/', { credentials: 'include' });
    if (r.ok) {
      const data = await r.json();
      // Homepage returns facility_id in store config
      const facilityId = data?.store?.facility_id;
      if (facilityId && facilityId !== 'null') {
        console.log('[FroggyAI] Resolved facility from homepage:', facilityId);
        return facilityId;
      }
      console.warn('[FroggyAI] Homepage response missing facility_id:', data);
    } else {
      console.warn('[FroggyAI] Homepage fetch failed with status:', r.status);
    }
  } catch (err) {
    console.warn('[FroggyAI] Homepage facility resolution failed:', err);
  }

  // 3) Final guard: return null - streaming will show user-friendly error
  console.error('[FroggyAI] Could not resolve facility ID - streaming will not work');
  return null;
}

export default function FroggyChatPageStreaming() {
  const { isAuthenticated } = useContext(AuthContext);
  const { selectedFacilityId } = useFacilityConsole();

  // Mount detection for debugging
  useEffect(() => {
    window.__froggyMount = true;
    console.log('[FroggyChatPageStreaming] Component mounted - streaming page active');
    return () => {
      window.__froggyMount = false;
    };
  }, []);

  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [lastUserMessage, setLastUserMessage] = useState('');
  const [showEscalate, setShowEscalate] = useState(false);

  // Voice input state
  const [voiceConfig, setVoiceConfig] = useState(null);
  const [isVoiceLoading, setIsVoiceLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const controllerRef = useRef(null);
  // Which turn the composer belongs to. A turn whose stream ends after a
  // NEWER turn started (Stop, then ask again) must not hand the composer
  // back under the newer one — its `await ask()` returns late.
  const turnSeqRef = useRef(0);
  const textareaRef = useRef(null);

  // Heartbeat for long silent gaps mid-stream — Sonnet + web_search
  // can run 30-60s with no visible token activity during the
  // Anthropic-side synthesis phase. Without this the user sees a
  // status delta ("Searching the web…") and then nothing, looks
  // frozen. Frontend-side timer is safer than refactoring the
  // Python sync generator to async (2026-05-22).
  const lastTokenAtRef = useRef(Date.now());
  const [silentSecs, setSilentSecs] = useState(0);

  // Media query to detect desktop/mobile
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 640);

  // Batch 34 C4 (Cowork run 32): THE CARD FITS THE VIEWPORT. The page was
  // `height: 100dvh` sitting UNDER the navbar, so its last navbar-height
  // (~97px at 1280) — the composer's Send — was below the fold at
  // 1280x958. The page's height is the viewport MINUS where the page
  // starts, measured (--ff-navbar-height is defined nowhere; see
  // CLAUDE.md, S3-06.3c), so the transcript scrolls and the composer is
  // always on screen.
  const chatPageRef = useRef(null);
  useEffect(() => {
    const el = chatPageRef.current;
    if (!el) return undefined;
    let raf = null;
    const fit = () => {
      const top = el.getBoundingClientRect().top + (window.scrollY || 0);
      el.style.setProperty('--ff-chat-page-top', `${Math.max(0, Math.round(top))}px`);
    };
    fit();
    raf = window.requestAnimationFrame ? window.requestAnimationFrame(fit) : null;
    window.addEventListener('resize', fit);
    return () => {
      window.removeEventListener('resize', fit);
      if (raf && window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 640px)');
    const handleChange = (e) => setIsDesktop(e.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Heartbeat tick — while streaming, recompute time-since-last-token
  // every 2s. Drives the "Still working… (Ns)" hint that appears
  // during long Sonnet+web_search silent gaps. No-op when not
  // streaming.
  useEffect(() => {
    if (!isStreaming) {
      setSilentSecs(0);
      return undefined;
    }
    const id = setInterval(() => {
      setSilentSecs(Math.floor((Date.now() - lastTokenAtRef.current) / 1000));
    }, 2000);
    return () => clearInterval(id);
  }, [isStreaming]);

  // Auto-resize textarea function
  const adjustHeight = (e) => {
    const textarea = e?.target || textareaRef.current;
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 192) + 'px';
  };

  // Auto-resize textarea on content change
  useEffect(() => {
    adjustHeight();
  }, [currentMessage]);

  // Auto-scroll to bottom WITHIN the chat-messages container when
  // a new message arrives. Two guards:
  //  - Skip when messages is empty so the welcome state doesn't
  //    drag the page to the bottom on mount (operator-reported,
  //    2026-05-27).
  //  - Use container.scrollTop directly instead of scrollIntoView
  //    so we never scroll the OUTER document — only the chat
  //    overflow area. scrollIntoView was bubbling up to the page
  //    scroll on small viewports / when the messages area wasn't
  //    a tall-enough overflow region.
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    const end = messagesEndRef.current;
    if (!end) return;
    const scroller = end.closest('.chat-messages');
    if (scroller) {
      scroller.scrollTop = scroller.scrollHeight;
    } else {
      scrollIntoViewMotionSafe(end, { block: 'nearest' });
    }
  }, [messages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // Fetch voice config on mount
  useEffect(() => {
    const loadVoiceConfig = async () => {
      try {
        const response = await getFroggyVoiceConfig();
        setVoiceConfig(response.data);
      } catch (err) {
        console.warn('[FroggyChat] Voice config fetch failed:', err);
        // Use defaults if config fetch fails
        setVoiceConfig({
          max_duration_seconds: 300,
          recommended_max_seconds: 75,
          max_file_size_mb: 25
        });
      }
    };
    loadVoiceConfig();
  }, []);

  // Check if user is authenticated (customer auth)
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const ask = async (question) => {
    // ⚠️ THE TURN EXISTS BEFORE ANYTHING IS AWAITED (third review). The
    // facility is resolved first, and that can be a network round trip; the
    // controller used to be created only after it, so a Stop during the
    // resolve found no controller, and the turn then streamed on with the
    // input unlocked — and a later send aborted it with its placeholder
    // still marked streaming, so the waiting indicator rotated on it
    // forever. Now the question, its placeholder and the controller all
    // exist up front: a Stop during the resolve stops THIS turn, and the
    // turn bails after the await without streaming.
    const userMessage = { id: nanoid(), role: 'user', content: question, timestamp: new Date() };
    const placeholder = { id: nanoid(), role: 'assistant', content: '', streaming: true, timestamp: new Date() };
    const idx = messages.length + 1; // user just appended
    setMessages(m => [...m, userMessage, placeholder]);

    const ctrl = new AbortController();
    controllerRef.current = ctrl;

    // ⚠️ A STOPPED TURN IS OVER. Stop (and a new send, and unmount) abort
    // `ctrl`; the abort closes the real SSE through the handle the service
    // hands `onOpen`, and every callback below checks `stopped()` first, so
    // a chunk already in flight can never flip the stopped message back to
    // streaming (which re-drew the waiting indicator under a stopped turn).
    const stopped = () => ctrl.signal.aborted;
    // …and whatever aborts it, THIS turn's own placeholder stops streaming,
    // found by id — never "the last message", which may be a newer turn's.
    ctrl.signal.addEventListener('abort', () => {
      setMessages(m => m.map(x => (
        x.id === placeholder.id && x.streaming
          ? { ...x, streaming: false, streamingStatus: null }
          : x
      )));
    }, { once: true });
    const onOpen = (handle) => {
      if (!handle || typeof handle.close !== 'function') return;
      if (stopped()) {
        handle.close();
        return;
      }
      ctrl.signal.addEventListener('abort', () => handle.close(), { once: true });
    };

    let facilityIdToUse;
    try {
      // Use FroggyAI facility resolution (never blocks the user)
      facilityIdToUse = await resolveFacilityId({ selectedFacilityId });
    } catch (facilityError) {
      console.warn('[FroggyChat] Facility resolution failed, using fallback:', facilityError);
      facilityIdToUse = null;
    }
    // Stopped (or replaced by a newer send) while the facility resolved:
    // nothing is streamed for it.
    if (stopped()) return;

    // Use resolved facility ID only (no ENV or global injection)
    const facilityId = facilityIdToUse;

    // Finalize helper to ensure proper cleanup
    const finalizeStream = (content = '') => {
      if (stopped()) return;
      setMessages(m => {
        const copy = [...m];
        copy[idx] = {
          ...copy[idx],
          streaming: false,
          content: (copy[idx].content || content).trim(),
          // Drop any transient status indicator that lingered until done.
          streamingStatus: null,
        };
        return copy;
      });
      setIsStreaming(false);
      if (controllerRef.current === ctrl) controllerRef.current = null;
    };

    let streamCompleted = false;
    let buf = '';

    // Message handler for unified contract
    const handleStreamMsg = (msg) => {
      if (stopped()) return;

      if (msg?.type === 'start') {
        // Stream started - no-op
        return;
      }

      if (msg?.type === 'status') {
        // Transient tool-execution status ("Finding a photo…",
        // "Searching the web…"). Rendered below the streaming
        // bubble as italic muted text and CLEARED when the first
        // real delta arrives. Never accumulated into the answer
        // body — Cowork QA 2026-05-25 caught the prior emission as
        // type='delta' leaking into the permanent bubble text.
        setMessages(m => {
          if (!m[idx] || !m[idx].streaming) return m;
          const copy = [...m];
          copy[idx] = { ...copy[idx], streamingStatus: msg.text || '' };
          return copy;
        });
        return;
      }

      if (msg?.type === 'delta') {
        // Accumulate text from delta events (handles both text and content fields)
        buf += msg.text ?? msg.content ?? '';
        // Reset the silent-gap heartbeat — a token just arrived.
        lastTokenAtRef.current = Date.now();

        // Update the assistant message in-place. Clear any prior
        // streamingStatus — the tool finished and real text is now flowing.
        // A message no longer streaming (stopped, errored, done) takes no
        // more chunks — it is never flipped back to streaming.
        setMessages(m => {
          if (!m[idx] || !m[idx].streaming) return m;
          const copy = [...m];
          copy[idx] = { ...copy[idx], content: buf, streamingStatus: null };
          return copy;
        });
        return;
      }

      if (msg?.type === 'done') {
        // Stream completed
        streamCompleted = true;
        finalizeStream(buf);
        return;
      }

      if (msg?.type === 'error') {
        throw new Error(msg.message || 'stream_failed');
      }
    };

    // Error handler
    const handleStreamError = (error) => {
      if (stopped()) return;
      console.error('[FroggyAI] Stream error:', error);
      finalizeStream(buf);
      // SSE-only mode: show error message
      setMessages((m) => {
        const copy = [...m];
        copy[idx] = {
          ...copy[idx],
          content: buf || 'Sorry, streaming is temporarily unavailable. Please try again.',
          streaming: false,
          error: true
        };
        return copy;
      });
      setError('Streaming connection failed. Please refresh and try again.');
    };

    // Complete handler
    const handleStreamComplete = (data) => {
      if (!streamCompleted) {
        streamCompleted = true;
        finalizeStream(buf);
      }
    };

    try {
      // Use the streamingAPI service with unified contract
      await streamingAPI.streamFroggyAI(
        facilityId,
        question,
        handleStreamMsg,
        handleStreamError,
        handleStreamComplete,
        false, // strict mode off: a pre-stream failure may retry (never once a stream opened)
        null, // conversationId (the page keeps the cookie conversation)
        onOpen,
      );
    } catch (e) {
      console.error('[FroggyAI] Stream setup error:', e);
      handleStreamError(e);
    } finally {
      // Ensure finalization on any exit without done
      if (!streamCompleted && controllerRef.current === ctrl) {
        finalizeStream(buf);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!currentMessage?.trim() || isStreaming) return;

    // Reset error state for new message
    setError(null);

    // Stop any active stream
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    setIsStreaming(true);
    // Reset the heartbeat clock so silentSecs starts at 0 for this
    // turn. The interval picks up from there. Reset state too —
    // without this, a rapid re-submit can flash a stale value from
    // the prior turn (e.g. "Still working… (47s)") for one tick
    // before the interval re-computes (adversarial review MED-2,
    // 2026-05-22).
    lastTokenAtRef.current = Date.now();
    setSilentSecs(0);

    // `ask()` handles its own cleanup via finalizeStream on every
    // happy + error path, but wrap it defensively: if ask ever throws
    // synchronously before any finalize runs, the user is left with
    // a stuck-on isStreaming flag and a disabled textarea. The finally
    // guarantees the input is usable again no matter what.
    const seq = ++turnSeqRef.current;
    try {
      await ask(userMessage);
    } catch (e) {
      console.error('[FroggyAI] ask() threw unexpectedly:', e);
    } finally {
      if (turnSeqRef.current === seq) {
        setIsStreaming(false);
        setLastUserMessage(userMessage);
      }
    }
  };

  const handleStopStream = () => {
    if (controllerRef.current) {
      // The abort is the whole stop: the turn's own listener marks ITS
      // placeholder not-streaming by id and closes the SSE (see ask()).
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setIsStreaming(false);
  };

  // Voice message handler - uses same streaming flow as text for consistent UX
  const handleVoiceMessage = async (audioBlob, duration) => {
    if (isStreaming || isVoiceLoading) return;

    setIsVoiceLoading(true);
    setError(null);

    try {
      // Resolve facility ID (same logic as text messages)
      let facilityId;
      try {
        facilityId = await resolveFacilityId({ selectedFacilityId });
      } catch (err) {
        console.warn('[FroggyChat] Voice facility resolution failed:', err);
        facilityId = null;
      }

      // Step 1: Get transcript from Whisper
      const response = await sendFroggyVoiceMessage(facilityId, audioBlob, duration);
      const { transcript } = response.data;

      if (!transcript || !transcript.trim()) {
        setError('Could not transcribe audio. Please try again.');
        return;
      }

      // Step 2: Use the same streaming flow as typed text
      // This gives the user the same waiting indicator and streaming experience
      setIsVoiceLoading(false);  // Turn off voice loading indicator
      setIsStreaming(true);
      setLastUserMessage(transcript);

      // Feed transcript into the existing streaming ask() function
      const seq = ++turnSeqRef.current;
      await ask(transcript);

      if (turnSeqRef.current === seq) setIsStreaming(false);

    } catch (err) {
      console.error('[FroggyChat] Voice message error:', err);
      const errorMessage = err.response?.data?.details || err.message || 'Voice message failed';
      setError(errorMessage);
    } finally {
      setIsVoiceLoading(false);
    }
  };

  return (
    <div className="froggy-chat-page" ref={chatPageRef}>
      <div className="page-container">
        <div className="page-header">
          <h1>
            <img
              src="/logos/ff original 500px.png"
              alt="Feeling Froggy logo"
              style={{
                height: '32px',
                width: 'auto',
                marginRight: '8px',
                verticalAlign: 'middle'
              }}
            />
            FroggyAI
          </h1>
          <p className="page-subtitle">AI-powered Froggy assistant to answer your questions</p>
        </div>

        <div className="chat-container">
          <div className="chat-messages">
            {messages.length === 0 && (
              <div className="welcome-message">
                <div className="welcome-bubble">
                  {/* Welcome copy mirrors the global FroggyChatBubble's
                      welcome so customers see the same scope/intent
                      whether they enter via the hamburger or the
                      site-wide bubble (operator alignment, 2026-05-27). */}
                  <p>
                    <strong>Hi! I'm FroggyAI</strong>{' '}
                    <img
                      src="/logos/ff_original_96px.png"
                      alt=""
                      className="froggy-welcome-logo"
                      data-froggy-welcome-logo
                      style={{ height: '1.25em', width: '1.25em', verticalAlign: 'middle' }}
                    />
                  </p>
                  <p>Ask about a species, care, shipping, or anything about Feeling Froggy.</p>
                  <p className="welcome-scope-note">
                    <span aria-hidden="true">💡</span>{' '}
                    <em>I can help with care for <strong>any frog, amphibian, or reptile</strong> — not just what we carry.</em>
                  </p>
                  <p><strong>Want to reach a human? Click "Talk to a human" at any time.</strong></p>
                </div>
              </div>
            )}

            {messages.map((message) => (
              <div key={message.id} className={`message ${message.role}`}>
                <div className="message-content">
                  <div className="message-text">
                    {message.role === 'assistant' && message.streaming && !message.content ? (
                      <FroggyWorking />
                    ) : message.role === 'assistant' ? (
                      // Render assistant messages as markdown so links
                      // are clickable, lists render, etc. User messages
                      // stay plain text — we don't want user input
                      // interpreted as markdown.
                      <ReactMarkdown components={MARKDOWN_COMPONENTS}>
                        {message.content || ''}
                      </ReactMarkdown>
                    ) : (
                      message.content
                    )}
                    {message.streaming && message.content && (
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    )}
                    {/* Transient tool-execution status (e.g.
                        "Finding a photo…"). Visible only while the
                        message is streaming AND streamingStatus is
                        set; cleared automatically when the first
                        real delta arrives. Same role/aria pattern as
                        the heartbeat hint so screen readers
                        announce status changes. (Cowork QA fix
                        2026-05-25.) */}
                    {message.streaming && message.streamingStatus && (
                      <div
                        className="streaming-status"
                        role="status"
                        aria-live="polite"
                      >
                        {message.streamingStatus}
                      </div>
                    )}
                    {/* Heartbeat hint — appears below the streaming
                        bubble after a long silent gap (typically
                        during Anthropic web_search synthesis). Only
                        renders on the LAST assistant message (the one
                        currently streaming) and only when silentSecs
                        exceeds the threshold. Reassures customers
                        that the bot hasn't frozen. Tool-agnostic
                        wording — the frontend doesn't know whether
                        web_search actually fired this turn, so we
                        avoid claiming "Searching the web…" as
                        factual (adversarial review HIGH-4,
                        2026-05-22). aria-live + role=status so
                        screen-reader users hear the update too
                        (review HIGH-3). */}
                    {message.streaming && silentSecs >= 8 && (
                      <div
                        className="heartbeat-hint"
                        role="status"
                        aria-live="polite"
                      >
                        {/* ONE neutral line (operator ruling 2026-10-10): FroggyAI
                            does not narrate a search or claim progress — the
                            frame cannot know either. Elapsed time is a fact;
                            that is all. */}
                        {`Still working… (${silentSecs}s)`}
                      </div>
                    )}
                  </div>
                  {message.citations && message.citations.length > 0 && (
                    <div className="message-citations">
                      <small>Sources: {message.citations.map(c => c.label).join(', ')}</small>
                    </div>
                  )}
                  <div className="message-time">
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}

            {error && (
              <div className="alert alert-danger mb-3">
                {error}
              </div>
            )}

                <div ref={messagesEndRef} />
              </div>

          <form onSubmit={handleSendMessage} className="chat-input-form">
            {/* Conditional render — ONE textarea in the DOM at a time.
                Prior dual-textarea-with-aria-hidden approach caused
                sporadic submit failures (focus/event-handler contention
                between the two textareas sharing state). Cowork E2E
                run 29 needed `form.requestSubmit()` JS fallback to
                work around it; this refactor removes the need.
                2026-05-22 — fix from beta QA round. */}
            {isDesktop ? (
              <div className="input-group-desktop">
                <textarea
                  ref={textareaRef}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onInput={adjustHeight}
                  placeholder={isStreaming ? "Streaming..." : "Ask me about species, care, shipping, or anything else..."}
                  className="form-control"
                  rows="2"
                  disabled={isStreaming}
                  aria-label="Message Froggy assistant"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <div className="button-group">
                  {/* Batch 34 C4: ONE primary — Send. Voice is a secondary,
                      "Talk to a human" stays an outline secondary, and all
                      three are the family's one 36px height. */}
                  <button
                    type="button"
                    className="ff-btn ff-btn--secondary escalate-btn"
                    onClick={() => setShowEscalate(true)}
                    title="Connect with human support"
                  >
                    Talk to a human
                  </button>
                  {voiceConfig && (
                    <VoiceRecorder
                      onVoiceMessage={handleVoiceMessage}
                      disabled={isStreaming || isVoiceLoading}
                      maxDurationSeconds={voiceConfig.max_duration_seconds}
                      recommendedMaxSeconds={voiceConfig.recommended_max_seconds}
                      variant="secondary"
                    />
                  )}
                  {isStreaming ? (
                    <button
                      type="button"
                      className="ff-btn ff-btn--danger"
                      onClick={handleStopStream}
                    >
                      Stop
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="ff-btn ff-btn--primary froggy-chat-send"
                      disabled={!currentMessage.trim()}
                      aria-disabled={!currentMessage.trim()}
                    >
                      Send
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="input-group-mobile">
                <textarea
                  ref={textareaRef}
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onInput={adjustHeight}
                  placeholder={isStreaming ? "Streaming..." : "Ask me about species, care, shipping, or anything else..."}
                  className="form-control textarea-mobile"
                  rows="2"
                  disabled={isStreaming}
                  aria-label="Message Froggy assistant"
                  style={{ fontSize: '16px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage(e);
                    }
                  }}
                />
                <div className="mobile-action-row">
                  <button
                    type="button"
                    className="ff-btn ff-btn--secondary escalate-btn-mobile"
                    onClick={() => setShowEscalate(true)}
                    title="Connect with human support"
                  >
                    Talk to a human
                  </button>
                  {voiceConfig && (
                    <VoiceRecorder
                      onVoiceMessage={handleVoiceMessage}
                      disabled={isStreaming || isVoiceLoading}
                      maxDurationSeconds={voiceConfig.max_duration_seconds}
                      recommendedMaxSeconds={voiceConfig.recommended_max_seconds}
                      variant="secondary"
                    />
                  )}
                  {isStreaming ? (
                    <button
                      type="button"
                      className="ff-btn ff-btn--danger send-btn-mobile"
                      onClick={handleStopStream}
                    >
                      Stop
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="ff-btn ff-btn--primary froggy-chat-send send-btn-mobile"
                      disabled={!currentMessage.trim()}
                      aria-disabled={!currentMessage.trim()}
                    >
                      Send
                    </button>
                  )}
                </div>
              </div>
            )}
            <small className="text-muted help-text">
              Press Enter to send • Shift+Enter for new line • Click "Talk to a human" to reach support
            </small>
          </form>
        </div>
      </div>

      <EscalateToSupportModal
        open={showEscalate}
        onClose={() => setShowEscalate(false)}
        prefillNotes={lastUserMessage}
      />

      <style jsx>{`
        .froggy-chat-page {
          padding: 1rem;
          max-width: 1000px;
          margin: 0 auto;
          /* Batch 34 C4: the viewport minus where the page starts (the
             navbar), measured in JS — the composer is always on screen. */
          height: calc(100dvh - var(--ff-chat-page-top, 0px));
          min-height: 420px;
          box-sizing: border-box;
          width: 100%;
          display: flex;
          flex-direction: column;
        }

        .page-container {
          background: white;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          height: 100%;
          display: flex;
          flex-direction: column;
        }

        @media (max-width: 639px) {
          .froggy-chat-page {
            padding: 0;
            height: calc(100dvh - var(--ff-chat-page-top, 0px));
          }

          .page-container {
            border-radius: 0;
            padding: 1rem;
          }
        }

        .page-header {
          margin-bottom: 1rem;
          border-bottom: 1px solid #e9ecef;
          padding-bottom: 1rem;
        }

        .page-header h1 {
          font-size: 1.75rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .page-subtitle {
          color: #6c757d;
          margin: 0;
        }

        .chat-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .chat-messages {
          flex: 1;
          overflow-y: auto;
          padding: 1rem 0;
          margin-bottom: 1rem;
        }

        .welcome-message {
          text-align: center;
          margin: 2rem 0;
        }

        .welcome-bubble {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1.5rem;
          border: 1px solid #e9ecef;
          display: inline-block;
          text-align: left;
          max-width: 500px;
        }

        .welcome-bubble ul {
          margin: 0.5rem 0;
          padding-left: 1.25rem;
        }

        /* Scope note mirrors the global FroggyChatBubble's
           .froggy-bubble-welcome-scope styling — soft inline italic
           aside (no box) so it reads as a natural follow-up sentence
           rather than an alert callout. Operator feedback 2026-05-27:
           the previous boxed treatment felt like a nested-box-within-
           a-box on the full-page chat. */
        .welcome-scope-note {
          margin: 0.35rem 0 0.75rem;
          font-size: 0.92rem;
          color: #555;
          line-height: 1.5;
        }

        .message {
          margin-bottom: 1rem;
          display: flex;
        }

        .message.user {
          justify-content: flex-end;
        }

        .message.assistant {
          justify-content: flex-start;
        }

        .message-content {
          max-width: 70%;
          padding: 0.75rem 1rem;
          border-radius: 12px;
          position: relative;
        }

        .message.user .message-content {
          /* Froggy blue with BLACK text (operator ruling 2026-10-10): cyan
             never takes white text, it is under 2:1. */
          background: var(--ff-brand-primary, #67E8F9);
          color: var(--ff-text-on-primary, #111111);
          border-bottom-right-radius: 4px;
        }

        .message.assistant .message-content {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-bottom-left-radius: 4px;
        }

        .message-text {
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        /* ReactMarkdown renders assistant messages — override the
           pre-wrap whitespace + tighten default paragraph/list spacing
           so the chat bubble stays compact. Links get the brand color
           and a clear underline on hover. */
        .message.assistant .message-text {
          white-space: normal;
        }
        .message.assistant .message-text p {
          margin: 0 0 0.5rem 0;
        }
        .message.assistant .message-text p:last-child {
          margin-bottom: 0;
        }
        .message.assistant .message-text ul,
        .message.assistant .message-text ol {
          margin: 0.25rem 0 0.5rem 0;
          padding-left: 1.25rem;
        }
        .message.assistant .message-text li {
          margin-bottom: 0.2rem;
        }
        .message.assistant .message-text a {
          color: var(--froggy-blue, #3EC4B5);
          text-decoration: underline;
          word-break: break-word;
        }
        .message.assistant .message-text a:hover {
          color: var(--froggy-blue-dark, #2da89c);
        }
        .message.assistant .message-text blockquote {
          margin: 0.5rem 0;
          padding-left: 0.75rem;
          border-left: 3px solid #dee2e6;
          color: #555;
        }
        .message.assistant .message-text code {
          background: #ececec;
          padding: 0.1rem 0.35rem;
          border-radius: 3px;
          font-size: 0.9em;
        }

        .message-citations {
          margin-top: 0.5rem;
          padding-top: 0.5rem;
          border-top: 1px solid rgba(255,255,255,0.2);
          font-style: italic;
        }

        .message.assistant .message-citations {
          border-top-color: #dee2e6;
        }

        .message-time {
          font-size: 0.75rem;
          opacity: 0.7;
          margin-top: 0.25rem;
        }

        .typing-indicator {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          margin-left: 0.5rem;
        }

        .heartbeat-hint {
          margin-top: 0.5rem;
          font-size: 0.85rem;
          color: #6c757d;
          font-style: italic;
          opacity: 0.85;
        }

        /* Transient status indicator emitted by tool-use status events.
           Matches the heartbeat-hint visual weight (muted italic) and
           disappears the moment real assistant text starts flowing. */
        .streaming-status {
          margin-top: 0.25rem;
          font-size: 0.85rem;
          color: #6c757d;
          font-style: italic;
          opacity: 0.85;
        }

        /* Inline images embedded by the assistant (P-08-Images).
           Capped to fit in the chat bubble without dominating it;
           lazy-loaded; soft rounded corners to match the bubble. */
        .message.assistant .message-text img.froggy-chat-img {
          display: block;
          max-width: 100%;
          max-height: 240px;
          border-radius: 8px;
          margin: 0.5rem 0;
          object-fit: cover;
        }

        .typing-indicator span {
          height: 8px;
          width: 8px;
          border-radius: 50%;
          background-color: #6c757d;
          animation: typing 1.4s infinite ease-in-out;
        }

        .typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
        .typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

        @keyframes typing {
          0%, 80%, 100% {
            transform: scale(0);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .chat-input-form {
          border-top: 1px solid #e9ecef;
          padding-top: 1rem;
        }

        /* Mobile layout: stacked */
        .input-group-mobile {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .input-group-desktop {
          display: none;
        }

        @media (min-width: 640px) {
          .input-group-mobile {
            display: none;
          }

          .input-group-desktop {
            display: flex;
            gap: 0.5rem;
          }
        }

        /* Desktop horizontal layout - matches prod exactly */
        .input-group-desktop textarea {
          flex: 1;
          resize: none;
          border-radius: 8px;
        }

        .button-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          align-self: flex-end;
          min-width: 140px;
        }

        /* Batch 34 C4: the three are the family's buttons (buttons.css
           owns height, radius and padding — one 36px height); the column
           only makes them one width. A padding override here is what made
           them 38-43px. */
        .button-group > button,
        .button-group .voice-recorder,
        .button-group .voice-recorder .voice-recorder-btn {
          width: 100%;
        }

        /* Mobile-specific styles */
        .escalate-btn-mobile {
          white-space: nowrap;
        }

        .textarea-mobile {
          width: 100%;
          min-height: 44px;
          max-height: 160px;
          resize: none;
          border-radius: 8px;
          padding: 0.75rem;
          font-size: 16px;
          line-height: 1.5;
        }

        .send-btn-mobile {
          white-space: nowrap;
        }

        .mobile-action-row {
          display: flex;
          gap: 0.5rem;
          align-items: center;
        }

        .help-text {
          display: block;
          margin-top: 0.5rem;
        }

        @media (max-width: 639px) {
          .help-text {
            font-size: 0.75rem;
          }

          .chat-input-form {
            position: sticky;
            bottom: 0;
            background: white;
            border-top: 1px solid #e9ecef;
            padding: 12px;
            padding-bottom: calc(12px + env(safe-area-inset-bottom));
            margin: 0 -1rem -1.5rem -1rem;
          }
        }

        .alert {
          padding: 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
          border: 1px solid transparent;
        }

        .alert-danger {
          color: #721c24;
          background-color: #f8d7da;
          border-color: #f5c6cb;
        }
      `}</style>
    </div>
  );
}